import React, {useContext, useEffect, useState} from 'react';
import {Button, Form} from 'react-bootstrap';
import {Controller, useForm} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddEditProps, EditInvoice, Invoice, InvoiceProtocolPick, MergeableSibling } from '../models/table-models';
import { InvoiceViewField } from './InvoiceView';
import CommonInputText from '../components/CommonInputText';
import { getViewEditInvoice } from '../utils/helper-functions';
import {
    EditFields,
    invoicePaymentOptions,
    invoiceStatus,
    invoiceTaskTypes
} from '../utils/Enums/InvoiceFields';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import {addNumberToInvoice, checkMergeableSiblings, saveProtocolPicks, mergeAndConfirmInvoices, confirmSaleInvoice} from "../utils/clients-hooks";
import {MergeInvoicesDialog} from "../components/MergeInvoicesDialog";
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import {DatePickerWrapper} from "../components/DatePickerWrapper";
import {NewRequestTableRow} from "../components/NewRequest/NewRequestTableRow";
import {InvoicePayments} from "../components/InvoicePayments";
import {InvoiceCorrectionInput} from '../models/invoice-corrections';
import {useInvoiceRegistryCheck} from '../components/useInvoiceRegistryCheck';

export const AddEditInvoice = ({
                                   itemToEdit,
                                   refetchEntitiesHook,
                                   taskType,
                                   useSubmit,
                                   setShowModal,
                               }: AddEditProps<Invoice> & {
    taskType?: string,
    useSubmit: (formValue: EditInvoice) => Promise<void>
}) => {
    const editInvoice = itemToEdit ? getViewEditInvoice(itemToEdit) : {} as EditInvoice;
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { t: newRequestT } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { hasPermission } = useContext(AuthContext);
    const { register, handleSubmit, formState, control, setValue, watch} = useForm<EditInvoice>({
        mode: 'all',
        defaultValues: { ...editInvoice },
    });

    const isFinancial = hasPermission([Role.FINANCIAL]);
    const isCoordinator = hasPermission([Role.COORDINATOR]);
    // Кредитирана и неплатена: затваря се с „Прихвани с КИ“ над формата, не с „Платено“ или плащане.
    const creditedUnpaid = itemToEdit?.document?.creditNoteNumber != null && !itemToEdit?.paid;

    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [mergeableSiblings, setMergeableSiblings] = useState<MergeableSibling[]>([]);
    const { checkBeforeIssue, registryDialog, registryBusy } = useInvoiceRegistryCheck();

    const addNumber = async (fields: Invoice | undefined) => {
        if (!fields?.id) return;
        await checkBeforeIssue(fields, () => continueNumbering(fields));
    };

    const continueNumbering = async (fields: Invoice) => {
        if (!fields.id) return;
        if (fields.isSaleInvoice) {
            await confirmSaleInvoice(fields.id);
            refetchEntitiesHook && refetchEntitiesHook();
            return;
        }
        const siblings = await checkMergeableSiblings(fields.id);
        if (fields.documentType === 'invoice' || (siblings && siblings.length > 0)) {
            setMergeableSiblings(siblings || []);
            setShowMergeDialog(true);
        } else {
            await confirmSingle(fields.id);
        }
    };

    const confirmSingle = async (invoiceId: number, corrections?: InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        if (corrections !== undefined || breakdowns?.length) await mergeAndConfirmInvoices([invoiceId], corrections, breakdowns);
        else await addNumberToInvoice(invoiceId);
        refetchEntitiesHook && refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('invoiceComfirm')]}/>, toastOptionsTopRight);
    };

    const confirmOnlyThis = async (picks: InvoiceProtocolPick[], corrections?: InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {
        if (picks.length > 0) {
            await saveProtocolPicks(picks);
        }
        itemToEdit?.id && await confirmSingle(itemToEdit.id, corrections, breakdowns);
        setShowMergeDialog(false);
    };

    const handleMergeSelected = async (invoicingIds: number[], picks: InvoiceProtocolPick[], corrections?: InvoiceCorrectionInput[], breakdowns?: import('../models/invoice-corrections').InvoiceBreakdownInput[]) => {

        // Запазваме избора преди издаването, за да няма издадена фактура при неуспешен запис на протоколите.
        if (picks.length > 0) {
            await saveProtocolPicks(picks);
        }

        await mergeAndConfirmInvoices(invoicingIds, corrections, breakdowns);
        setShowMergeDialog(false);

        refetchEntitiesHook && refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('invoiceComfirm')]}/>, toastOptionsTopRight);
    };

    useEffect(() => {
        const discount = watch("discount") ?? 0;
        if (itemToEdit?.total !== undefined) {
            const newTotal = itemToEdit.total/100 * (1 - discount / 100);
            setValue(
                "total",
                Math.round((newTotal + Number.EPSILON) * 100) / 100
            );
        }
    }, [watch("discount")]);

    return (
        <>
            {registryDialog}
            <MergeInvoicesDialog
                show={showMergeDialog}
                onHide={() => setShowMergeDialog(false)}
                currentInvoice={itemToEdit}
                siblings={mergeableSiblings}
                onConfirmOnlyThis={confirmOnlyThis}
                onMergeSelected={handleMergeSelected}
            />
            <Form
                id='form'
                className='d-flex flex-column align-items-center p-4  w-100'
                noValidate
                onSubmit={itemToEdit && handleSubmit(useSubmit)}
            >
                <div className='mb-3 w-100 border-bottom'>
                    <div className='w-100 d-flex flex-wrap mb-3 justify-content-between'>
                        <div className='flex-column w-50 pe-lg-5'>
                            {itemToEdit &&
                                EditFields.left.map((value, index) => (
                                    <InvoiceViewField
                                        key={`${index}-left`}
                                        fieldName={value as keyof Invoice}
                                        {...{ index }}
                                        item={itemToEdit}
                                    />
                                ))}
                        </div>
                        <div className='flex-column w-50 pe-lg-5'>
                            {itemToEdit && (itemToEdit.document?.invoiceNumber!=null || itemToEdit.isSaleInvoice) ?
                                EditFields.right.map((value, index) => (
                                    <InvoiceViewField
                                        key={`${index}-right`}
                                        fieldName={value as keyof Invoice}
                                        {...{ index }}
                                        item={itemToEdit}
                                    />
                                ))
                            :
                                <div>
                                    <NewRequestTableRow
                                        id='creationDate'
                                        label={t('creationDate')}
                                        inputContainerClass='w-50'
                                        labelContainerClass='w-50'
                                    >
                                            <Controller
                                                name='creationDate'
                                                {...{control}}
                                                render={({field: {value}}) => (
                                                    <DatePickerWrapper
                                                        selected={value ?? ''}
                                                        onChange={(value) => {
                                                            setValue("creationDate", value)
                                                        }}
                                                    />
                                                )}
                                            />
                                    </NewRequestTableRow>
                                    <NewRequestTableRow
                                        id='fromDate'
                                        label={t('fromDate')}
                                        inputContainerClass='w-50'
                                        labelContainerClass='w-50'
                                    >
                                        <Controller
                                            name='fromDate'
                                            {...{control}}
                                            render={({field: {value}}) => (
                                                <DatePickerWrapper
                                                    selected={value ?? ''}
                                                    onChange={(value) => {
                                                        setValue("fromDate", value)
                                                    }}
                                                />
                                            )}
                                        />
                                    </NewRequestTableRow>
                                    <NewRequestTableRow
                                        id='duePaymentDate'
                                        label={t('duePaymentDate')}
                                        inputContainerClass='w-50'
                                        labelContainerClass='w-50'
                                    >
                                        <Controller
                                            name='duePaymentDate'
                                            {...{control}}
                                            render={({field: {value}}) => (
                                                <DatePickerWrapper
                                                    selected={value ?? ''}
                                                    onChange={(value) => {
                                                        setValue("duePaymentDate", value)
                                                    }}
                                                />
                                            )}
                                        />
                                    </NewRequestTableRow>
                                </div>
                            }
                        </div>
                    </div>
                </div>

                {itemToEdit && !itemToEdit.isSaleInvoice && itemToEdit.paymentMethod === 'bank'
                    && itemToEdit.document?.invoiceNumber != null && !itemToEdit.creditNoteOffset
                    // Кредитирана: само ако има въведени плащания, и то за преглед.
                    && (!creditedUnpaid || (itemToEdit.document?.paidAmount ?? 0) > 0)
                    && (isFinancial || isCoordinator) && (
                    <InvoicePayments invoice={itemToEdit} refetch={refetchEntitiesHook} readOnly={creditedUnpaid}
                                     onInvoiceClosed={() => setShowModal?.(false)} />
                )}

                <div className='w-100 d-flex mb-3 pb-3 border-bottom'>
                    <div className='w-50 d-inline-block m-0 p-1'>
                        <label className='mb-2'>{t('paid type')}</label>
                        <Form.Select {...register('paid')}>
                            {invoicePaymentOptions.map((item, index) => (
                                <option key={index} value={item.value}
                                        disabled={(itemToEdit?.paid && itemToEdit.paid && ( !isCoordinator || !isFinancial )) || itemToEdit?.isSaleInvoice || itemToEdit?.document?.invoiceNumber!=null}>
                                    {newRequestT(item.name)}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    {(isFinancial || isCoordinator) && (
                        <div className='w-50 d-inline-block m-0 p-1'>
                            <label className='mb-2'>{t('state')}</label>
                            <Form.Select {...register('isPaid')} >
                                {invoiceStatus.map((option, index) => (
                                    <option key={index} value={option.value}
                                            disabled={ itemToEdit?.document?.invoiceNumber==null || (itemToEdit?.paid && itemToEdit.paid) || creditedUnpaid}>
                                        {option.name && t(option.name)}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                    {taskType != undefined && (
                        <div className='w-50 d-inline-block m-0 p-1'>
                            <Form.Label>{t('taskType')}</Form.Label>
                            <Form.Select {...register('taskType')}>
                                {invoiceTaskTypes.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {t(item.name.toLowerCase())}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                </div>
                <div className='w-100 d-flex flex-column'>
                    <div className='w-100 d-flex flex-wrap mb-3'>
                        <div className='w-50 d-flex flex-column '>
                            <Form.Group controlId='total'>
                                <CommonInputText
                                    id='total'
                                    label={t('total') + '  (' + itemToEdit?.currency + ')'}
                                    labelTextColor='text-black'
                                    type='number'
                                    disabled={( itemToEdit?.paid && itemToEdit.paid ) || itemToEdit?.document?.invoiceNumber!=null
                                        || (itemToEdit?.paidAmount ?? 0) > 0}
                                    fieldError={formState.errors?.total}
                                    hookRegister={register('total', {
                                        required: {
                                            value: true,
                                            message: t('total'),
                                        },
                                        min: {
                                            value: 0,
                                            message: t('total'),
                                        },
                                    })}
                                />
                            </Form.Group>
                        </div>
                        <div className={"w-50 align-content-center mt-4"}>
                            {itemToEdit?.document?.invoiceNumber == undefined && itemToEdit?.paymentMethod=="bank" && <Button disabled={registryBusy} className={"btn-success"} onClick={()=>addNumber(itemToEdit)}>{t('confirm')}</Button>}
                        </div>
                    </div>
                    {itemToEdit?.isSaleInvoice &&
                        <div className='w-50 d-flex flex-column '>
                        <Form.Group controlId='discount'>
                            <CommonInputText
                                id='discount'
                                label={t('discount') + '  (%)'}
                                labelTextColor='text-black'
                                type='number'
                                disabled={( itemToEdit?.paid && itemToEdit.paid ) || itemToEdit?.document?.invoiceNumber!=null}
                                fieldError={formState.errors?.discount}
                                hookRegister={register('discount', {
                                    required: {
                                        value: false,
                                        message: t('discount'),
                                    },
                                    min: {
                                        value: 0,
                                        message: t('discount'),
                                    },
                                })}
                            />
                        </Form.Group>
                        </div>
                    }
                    {!itemToEdit?.isSaleInvoice && <Form.Group controlId='comment'>
                        <CommonInputText
                            id='comment'
                            label={t('textInvoice')}
                            labelTextColor='text-black'
                            type='text'
                            fieldError={formState.errors?.comment}
                            hookRegister={register('comment', {
                                 required: {
                                     value: true,
                                     message: t('comment'),
                                 },
                            })}
                        />
                    </Form.Group>}
                </div>
            </Form>
        </>
    );
};
